import { useCallback, useContext, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import polylabel from "polylabel";
import { parse as WKTparse } from "wkt";
import { Localization } from "@viamap/viamap2-common";
import { SearchBarGeneral } from "src/components/SearchBarGeneral";
import { message } from "src/components/Message";
import { AddressAutocomplete, BFELookup } from "src/managers/SearchBarGeneralPlugin";
import { LayerFunc } from "src/managers/LayerFunc";
import { MitLatLng } from "src/managers/MapFacade";
import type { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { DataCollector, DC } from "src/dataCollector/DataCollector";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { MapitStateContext, MapitStateActionType, actionUpdateDataLayer } from "src/states/MapitState";
import { feature } from "@turf/helpers";
import { GlassButton } from "src/components/GlassButtons";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import type { LayerInfo } from "src/common/managers/Types";

type BfeSearchData = {
   BFEnummer: number;
   SFEnummer?: string | number;
   type: "SFE" | "BPFG" | "Ejerlejlighed";
   geometri?: string;
};

function pointLatLngFromLayerInfo(li: LayerInfo): { lat: number; lng: number } | null {
   const coords = li.geoJson?.features?.[0]?.geometry?.coordinates;
   if (!Array.isArray(coords) || coords.length < 2) {
      return null;
   }
   const lng = Number(coords[0]);
   const lat = Number(coords[1]);
   if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
   }
   return { lat, lng };
}

export function JPBSimpleSearchHeader(props: {
   mapFacade: MapFacadeMaplibre;
   onPinLayerReady: (layerId: number) => void;
   onShowAdvanced: () => void;
   resolvedAddressLine: string | null;
   addressLineResolving: boolean;
   setResolvedAddressLine: Dispatch<SetStateAction<string | null>>;
   setAddressLineResolving: Dispatch<SetStateAction<boolean>>;
}) {
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const pinLayerIdRef = useRef<number | null>(null);
   const addressResolveSeqRef = useRef(0);
   const { setResolvedAddressLine, setAddressLineResolving, resolvedAddressLine, addressLineResolving } = props;

   const resolveAndShowAddressForPin = useCallback((li: LayerInfo) => {
      const pt = pointLatLngFromLayerInfo(li);
      if (!pt) {
         setResolvedAddressLine(null);
         setAddressLineResolving(false);
         return;
      }
      const seq = ++addressResolveSeqRef.current;
      setAddressLineResolving(true);
      setResolvedAddressLine(null);
      DC.ADR.reverseGeocode(pt.lat, pt.lng).then((line) => {
         if (addressResolveSeqRef.current !== seq) {
            return;
         }
         setResolvedAddressLine(line ?? null);
      }).finally(() => {
         if (addressResolveSeqRef.current === seq) {
            setAddressLineResolving(false);
         }
      });
   }, [setResolvedAddressLine, setAddressLineResolving]);

   const placeOrUpdatePin = useCallback(
      (freshLayerInfo: ReturnType<typeof LayerFunc.createLayerInfoForOnePoint>) => {
         const zoomTo = () => {
            try {
               const f = freshLayerInfo.geoJson?.features?.[0];
               if (f?.geometry) {
                  props.mapFacade.zoomToData(feature(f.geometry), 150);
               }
            } catch {
               /* ignore zoom errors */
            }
         };

         resolveAndShowAddressForPin(freshLayerInfo);

         if (pinLayerIdRef.current === null) {
            mapitStateDispatch({
               type: MapitStateActionType.AddDataLayer,
               payload: { layerInfo: freshLayerInfo, zoomToFocus: false, showAsDefault: true },
               callbacks: {
                  onLayerAdded: (created) => {
                     pinLayerIdRef.current = created.layerId;
                     props.onPinLayerReady(created.layerId);
                     zoomTo();
                  }
               }
            } as any);
            return;
         }

         const id = pinLayerIdRef.current;
         const cur = mapitState.layers[id];
         if (!cur) {
            pinLayerIdRef.current = null;
            mapitStateDispatch({
               type: MapitStateActionType.AddDataLayer,
               payload: { layerInfo: freshLayerInfo, zoomToFocus: false, showAsDefault: true },
               callbacks: {
                  onLayerAdded: (created) => {
                     pinLayerIdRef.current = created.layerId;
                     props.onPinLayerReady(created.layerId);
                     zoomTo();
                  }
               }
            } as any);
            return;
         }

         mapitStateDispatch(
            actionUpdateDataLayer(id, {
               ...cur,
               geoJson: freshLayerInfo.geoJson,
               datasetname: freshLayerInfo.datasetname,
               propertiesInGeoJson: freshLayerInfo.propertiesInGeoJson,
               propertiesToDisplay: freshLayerInfo.propertiesToDisplay
            })
         );
         props.onPinLayerReady(id);
         zoomTo();
      },
      [mapitState.layers, mapitStateDispatch, props, resolveAndShowAddressForPin]
   );

   const onBfeSelected = useCallback(
      async (data: BfeSearchData) => {
         ActionTracker.track("JPBModule", "Search", "BFE");
         const layerName =
            data.BFEnummer +
            ` (${data.type === "Ejerlejlighed" ? Localization.getText("Condominium") : data.type})`;

         if (data.geometri) {
            const tmp = WKTparse(data.geometri);
            if (tmp?.coordinates[0]) {
               const coords = GenerateGeomUtils.convertFromUTMtoLatLng({
                  x: tmp.coordinates[0],
                  y: tmp.coordinates[1]
               });
               placeOrUpdatePin(
                  LayerFunc.createLayerInfoForOnePoint(new MitLatLng(coords.lat, coords.lng), layerName, { ...data })
               );
               return;
            }
         }

         const dataCopy = { ...data };
         if ("geometri" in dataCopy) {
            delete (dataCopy as BfeSearchData).geometri;
         }

         if (dataCopy.type) {
            const EBR = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([dataCopy.BFEnummer.toString()]);
            if (EBR[0]?.geometri) {
               const tmp = WKTparse(EBR[0]?.geometri);
               if (tmp?.coordinates[0]) {
                  const coords = GenerateGeomUtils.convertFromUTMtoLatLng({
                     x: tmp.coordinates[0],
                     y: tmp.coordinates[1]
                  });
                  placeOrUpdatePin(
                     LayerFunc.createLayerInfoForOnePoint(new MitLatLng(coords.lat, coords.lng), layerName, { ...dataCopy })
                  );
                  return;
               }
            }
         }

         if (dataCopy.type === "SFE") {
            const geoJson = await PropertyInfoInterface.getGeojsonOfSFE(dataCopy.BFEnummer);
            const coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001);
            placeOrUpdatePin(
               LayerFunc.createLayerInfoForOnePoint(
                  new MitLatLng(coords[1], coords[0]),
                  layerName,
                  { ...geoJson.features[0].properties }
               )
            );
            return;
         }

         message.info(Localization.getText("No Position found for BFE"));
      },
      [placeOrUpdatePin]
   );

   const onAddressSelected = useCallback(
      async (data: any) => {
         ActionTracker.track("JPBModule", "Search", "Adresse");
         const wgsCoords = data.geometri.coordinates[0].toReversed();
         const layerInfo = LayerFunc.createLayerInfoForOneAddressV2(data.vejnavn, data.husnummer, wgsCoords.reverse());
         placeOrUpdatePin(layerInfo);
      },
      [placeOrUpdatePin]
   );

   const autocompleters = useMemo(
      () => [new BFELookup(onBfeSelected), new AddressAutocomplete(onAddressSelected)],
      [onBfeSelected, onAddressSelected]
   );

   return (
      <div className="jpb-simple-search-header">
         <div className="jpb-simple-search-header__top">
            <div className="jpb-simple-search-header__search">
               <SearchBarGeneral
                  showWindow={true}
                  autocompleters={autocompleters}
                  callbackOnSelection={() => { }}
               />
            </div>
            <GlassButton
               className="jpb-view-mode-btn"
               title={Localization.getText("Advanced view")}
               onClick={() => props.onShowAdvanced()}
            >
               <MIBIcon icon="advancedMode" />
               <span className="jpb-view-mode-btn__label">{Localization.getText("Advanced")}</span>
            </GlassButton>
         </div>
         {(addressLineResolving || resolvedAddressLine) ? (
            <div className="jpb-simple-search-header__resolved-address Line-MitProp">
               <div>{Localization.getText("Address")}</div>
               <div>{addressLineResolving && !resolvedAddressLine ? "…" : resolvedAddressLine}</div>
            </div>
         ) : null}
      </div>
   );
}
